import axios from 'axios';
import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { isEmpty } from 'lodash/fp';

const useFetch = ({ url, baseURL = '/bff', method = 'GET', immediate = true, useCache = true, ...options } = {}) => {
	const { token, cancel } = useMemo(() => axios.CancelToken.source(), []);

	const instance = axios.create({
		baseURL,
		cancelToken: token,
		xsrfCookieName: 'react-session',
		xsrfHeaderName: 'csrf-token',
	});

	const cache = useRef(() => {});

	const initialState = {
		status: 'init',
		error: undefined,
		data: undefined,
	};

	const fetchReducer = (state, { type, payload }) => {
		if (type === 'request') return { ...initialState, status: 'fetching' };
		if (type === 'success') return { ...initialState, status: 'fetched', data: payload };
		if (type === 'failure') return { ...initialState, status: 'error', error: payload };

		return state;
	};

	const [state, dispatch] = useReducer(fetchReducer, initialState);

	const fetchData = useCallback(async (additionalOptions = {}) => {
		dispatch({ type: 'request' });

		try {
			if (cache.current[url] && isEmpty(additionalOptions)) return dispatch({ type: 'success', payload: cache.current[url] });

			const { data } = await instance(url, { method, ...options, ...additionalOptions });

			if (useCache) cache.current[url] = data;

			dispatch({ type: 'success', payload: data });
		} catch (err) {
			if (axios.isCancel(err)) return; //component unmounted.

			dispatch({ type: 'failure', payload: err.message });
		}
	}, []); // eslint-disable-line

	useEffect(() => {
		if (immediate) fetchData();

		return () => cancel('Component unmounted!');
	}, [immediate, fetchData, cancel]);

	return { ...state, fetchData };
};

export default useFetch;
